import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, FormControlLabel,
  List, ListItemButton, ListItemAvatar, ListItemText, Stack,
  Switch, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined'
import type { InviteRequest, InviteRequestStatus, SuperAdminOrg } from '@cmms/api-client'

import { api } from '../../lib/api'
import { APP_NAME } from '../../lib/branding'
import { toast } from '../../store/snackbarStore'
import { useAuthStore } from '../../store/authStore'
import { useOrgSettings } from '../../hooks/useOrgSettings'

const TABS: { value: InviteRequestStatus | 'all'; label: string }[] = [
  { value: 'pending',  label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'declined', label: 'Declined' },
  { value: 'all',      label: 'All' },
]

export function InviteRequestsTab() {
  const qc = useQueryClient()
  const [tab, setTab] = useState<InviteRequestStatus | 'all'>('pending')
  const [switchOpen, setSwitchOpen] = useState(false)

  const list = useQuery({
    queryKey: ['invite-requests', tab],
    queryFn: () =>
      api.superAdmin.listInviteRequests(
        tab === 'all' ? undefined : tab,
      ),
  })

  const items = list.data ?? []

  const counts = useMemo(() => {
    if (!list.data) return { pending: 0, approved: 0, declined: 0 }
    // When tab is filtered the totals per status aren't available; just
    // show the live count for the active tab.
    return {
      pending:  items.filter((r) => r.status === 'pending').length,
      approved: items.filter((r) => r.status === 'approved').length,
      declined: items.filter((r) => r.status === 'declined').length,
    }
  }, [items, list.data])

  const approve = useMutation({
    mutationFn: (id: string) => api.superAdmin.approveInviteRequest(id),
    onSuccess: (res) => {
      toast.success(`Approved — code ${res.code} emailed`)
      qc.invalidateQueries({ queryKey: ['invite-requests'] })
    },
    onError: (e: unknown) => toast.error((e as Error).message),
  })

  const resend = useMutation({
    mutationFn: (id: string) => api.superAdmin.resendInviteCode(id),
    onSuccess: (res) => {
      toast.success(
        res.reissued
          ? `Old code expired — issued a new one (${res.code}) and emailed it`
          : `Code ${res.code} re-emailed`,
      )
      qc.invalidateQueries({ queryKey: ['invite-requests'] })
    },
    onError: (e: unknown) => toast.error((e as Error).message),
  })

  const [declineFor, setDeclineFor] = useState<InviteRequest | null>(null)
  const [declineReason, setDeclineReason] = useState('')
  const [declineNotify, setDeclineNotify] = useState(false)

  const decline = useMutation({
    mutationFn: () => api.superAdmin.declineInviteRequest(declineFor!.id, {
      reason: declineReason.trim() || undefined,
      notify: declineNotify,
    }),
    onSuccess: () => {
      toast.success('Request declined')
      setDeclineFor(null)
      setDeclineReason('')
      setDeclineNotify(false)
      qc.invalidateQueries({ queryKey: ['invite-requests'] })
    },
    onError: (e: unknown) => toast.error((e as Error).message),
  })

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>
            Workspace signup requests
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Review and approve people who want to create a {APP_NAME} workspace. Approving
            generates an invite code and emails it to them.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            onClick={() => setSwitchOpen(true)}
            startIcon={<SwapHorizOutlinedIcon sx={{ fontSize: 16 }} />}
            variant="outlined" size="small"
            sx={{
              textTransform: 'none', borderRadius: 999,
              fontWeight: 600, fontSize: 12.5, whiteSpace: 'nowrap',
            }}
          >
            Switch workspace
          </Button>
          <Tooltip title="Refresh">
            <Button
              onClick={() => list.refetch()}
              startIcon={<RefreshOutlinedIcon sx={{ fontSize: 16 }} />}
              variant="text"
              sx={{ color: 'text.secondary', textTransform: 'none' }}
            >
              Refresh
            </Button>
          </Tooltip>
        </Stack>
      </Stack>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as InviteRequestStatus | 'all')}
        sx={{
          mb: 2, minHeight: 36,
          '& .MuiTab-root': {
            minHeight: 36, textTransform: 'none', fontSize: 13,
            fontWeight: 600, mr: 2.5, px: 0,
          },
          '& .MuiTabs-indicator': { height: 2 },
        }}
      >
        {TABS.map((t) => {
          const c = t.value === 'pending' ? counts.pending
                  : t.value === 'approved' ? counts.approved
                  : t.value === 'declined' ? counts.declined
                  : items.length
          return (
            <Tab key={t.value} value={t.value}
              label={c > 0 ? `${t.label} (${c})` : t.label} />
          )
        })}
      </Tabs>

      {list.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : items.length === 0 ? (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Box sx={{
            display: 'inline-flex', width: 56, height: 56,
            borderRadius: 99, bgcolor: 'action.hover',
            alignItems: 'center', justifyContent: 'center', mb: 2,
          }}>
            <MarkEmailReadOutlinedIcon sx={{ fontSize: 26, color: 'text.secondary' }} />
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            {tab === 'pending'
              ? 'No requests waiting for review.'
              : 'Nothing here yet.'}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.5 }}>
            New signup requests will show up here for approval.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {items.map((req) => (
            <Row
              key={req.id}
              req={req}
              onApprove={() => approve.mutate(req.id)}
              onDecline={() => setDeclineFor(req)}
              onResend={() => resend.mutate(req.id)}
              busy={
                (approve.isPending && approve.variables === req.id) ||
                (resend.isPending  && resend.variables  === req.id)
              }
            />
          ))}
        </Stack>
      )}

      <Dialog open={!!declineFor} onClose={() => setDeclineFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Decline this request?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13.5, mb: 2 }}>
            {declineFor?.email} won't receive an invite code. You can include
            an optional reason that will be emailed to them only if you flip
            the toggle below.
          </DialogContentText>
          <TextField
            label="Reason (optional)"
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            fullWidth multiline minRows={2} maxRows={4}
            sx={{ mb: 1 }}
          />
          <FormControlLabel
            control={<Switch checked={declineNotify}
              onChange={(e) => setDeclineNotify(e.target.checked)} />}
            label="Email the requester"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeclineFor(null)}>Cancel</Button>
          <Button
            onClick={() => decline.mutate()}
            disabled={decline.isPending}
            color="error" variant="contained"
            sx={{ borderRadius: 999, px: 2.5 }}
          >
            {decline.isPending ? 'Declining…' : 'Decline'}
          </Button>
        </DialogActions>
      </Dialog>

      <SwitchWorkspaceDialog
        open={switchOpen}
        onClose={() => setSwitchOpen(false)}
      />
    </Box>
  )
}

// ─── Switch workspace dialog ────────────────────────────────────────────────
//
// Lets a super admin (with or without an active org context) jump into
// any org via the registry list. Reuses the existing /super-admin/orgs
// endpoint and the auth.switchOrg flow that was already plumbed for the
// login fallback path.

function SwitchWorkspaceDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const setTokens = useAuthStore((s) => s.setTokens)
  const setUser = useAuthStore((s) => s.setUser)

  const orgs = useQuery({
    queryKey: ['super-admin', 'orgs'],
    queryFn: () => api.superAdmin.listAllOrgs(),
    enabled: open,
  })

  const switching = useMutation({
    mutationFn: async (org: SuperAdminOrg) => {
      const res = await api.auth.switchOrg({ org_slug: org.slug })
      setTokens(res.access_token, '')
      const me = await api.auth.me()
      setUser(me)
      return org
    },
    onSuccess: (org) => {
      toast.success(`Switched to ${org.name}`)
      onClose()
      navigate('/dashboard')
    },
    onError: (e: unknown) => toast.error((e as Error).message),
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>Switch workspace</DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <DialogContentText sx={{ fontSize: 13, mb: 1.5 }}>
          Pick a workspace to jump into. You'll keep your super-admin
          token but everything else (work orders, assets, settings) will
          be scoped to that org.
        </DialogContentText>

        {orgs.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        ) : (orgs.data?.length ?? 0) === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              No workspaces yet. Approve a signup request and you'll have one.
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {orgs.data!.map((org) => (
              <ListItemButton
                key={org.id}
                onClick={() => switching.mutate(org)}
                disabled={switching.isPending}
                sx={{ borderRadius: 1.5, mb: 0.5 }}
              >
                <ListItemAvatar>
                  <Avatar sx={{
                    width: 34, height: 34,
                    bgcolor: 'action.hover', color: 'text.secondary',
                  }}>
                    <BusinessOutlinedIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                      {org.name}
                    </Typography>
                  }
                  secondary={
                    <Typography sx={{
                      fontSize: 12, color: 'text.secondary',
                      fontFamily: 'ui-monospace, monospace',
                    }}>
                      {org.slug}
                    </Typography>
                  }
                />
                {switching.isPending && switching.variables?.id === org.id && (
                  <CircularProgress size={16} />
                )}
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

function Row({
  req, onApprove, onDecline, onResend, busy,
}: {
  req: InviteRequest
  onApprove: () => void
  onDecline: () => void
  onResend: () => void
  busy: boolean
}) {
  const { formatDate } = useOrgSettings()
  const initials = req.email.slice(0, 2).toUpperCase()
  const requestedAt = formatDate(req.requested_at, true)
  const isPending = req.status === 'pending'
  const isApproved = req.status === 'approved'

  const statusChip = (() => {
    if (req.status === 'approved') {
      return <Chip size="small" color="success" variant="outlined"
        label="Approved" sx={{ fontSize: 11, fontWeight: 700 }} />
    }
    if (req.status === 'declined') {
      return <Chip size="small" color="error" variant="outlined"
        label="Declined" sx={{ fontSize: 11, fontWeight: 700 }} />
    }
    return null
  })()

  // Code state — only meaningful for approved rows.
  const codeUsed    = !!req.invite_code_used_at
  const codeExpired = !codeUsed && !!req.invite_code_expires_at
    && new Date(req.invite_code_expires_at) <= new Date()
  const codeActive  = isApproved && !!req.invite_code && !codeUsed && !codeExpired

  return (
    <Box sx={{
      p: 2, borderRadius: 2, border: '1px solid',
      borderColor: 'divider', bgcolor: 'background.paper',
      display: 'flex', alignItems: 'center', gap: 2,
    }}>
      <Avatar sx={{
        bgcolor: isPending ? 'primary.light' : 'action.hover',
        color:   isPending ? 'primary.main'  : 'text.secondary',
        fontSize: 13, fontWeight: 700, width: 38, height: 38,
      }}>
        {initials}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <Typography sx={{
            fontSize: 14, fontWeight: 700, letterSpacing: '-0.005em',
          }} noWrap>
            {req.email}
          </Typography>
          {statusChip}
          {isApproved && req.invite_code && (
            <CodeChip
              code={req.invite_code}
              used={codeUsed}
              expired={codeExpired}
              expiresAt={req.invite_code_expires_at}
            />
          )}
        </Stack>
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>
          Requested {requestedAt}
          {req.reviewed_by ? ` · reviewed by ${req.reviewed_by}` : ''}
        </Typography>
        {req.note && (
          <Typography sx={{
            fontSize: 12, color: 'text.secondary', mt: 0.75,
            fontStyle: 'italic',
          }}>
            "{req.note}"
          </Typography>
        )}
        {req.decline_reason && (
          <Typography sx={{
            fontSize: 12, color: 'error.main', mt: 0.75,
          }}>
            Reason: {req.decline_reason}
          </Typography>
        )}
      </Box>

      {isPending && (
        <Stack direction="row" spacing={1}>
          <Button
            onClick={onDecline}
            startIcon={<CancelOutlinedIcon sx={{ fontSize: 16 }} />}
            variant="text" color="inherit"
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Decline
          </Button>
          <Button
            onClick={onApprove}
            disabled={busy}
            startIcon={busy
              ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
              : <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
            variant="contained" color="primary"
            sx={{ textTransform: 'none', borderRadius: 999, px: 2.5 }}
          >
            {busy ? 'Approving…' : 'Approve'}
          </Button>
        </Stack>
      )}

      {isApproved && !codeUsed && (
        <Tooltip title={
          codeActive
            ? 'Email this code to the user again'
            : 'Issue a new code and email it'
        }>
          <span>
            <Button
              onClick={onResend}
              disabled={busy}
              startIcon={busy
                ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
                : <ForwardToInboxOutlinedIcon sx={{ fontSize: 16 }} />}
              variant="outlined"
              sx={{
                textTransform: 'none', borderRadius: 999, px: 2.5,
                whiteSpace: 'nowrap',
              }}
            >
              {busy ? 'Sending…' : codeActive ? 'Resend code' : 'Reissue code'}
            </Button>
          </span>
        </Tooltip>
      )}
    </Box>
  )
}

function CodeChip({
  code, used, expired, expiresAt,
}: {
  code: string
  used: boolean
  expired: boolean
  expiresAt: string | null
}) {
  const tone = used    ? { bg: '#F3F4F6', fg: '#6B7280', label: 'Used' }
             : expired ? { bg: '#FEF3C7', fg: '#92400E', label: 'Expired' }
                       : { bg: '#ECFDF5', fg: '#059669', label: 'Active' }

  const expiresIn = (() => {
    if (used || expired || !expiresAt) return null
    const ms = new Date(expiresAt).getTime() - Date.now()
    if (ms <= 0) return null
    const h = Math.floor(ms / 3_600_000)
    if (h >= 1) return `expires in ${h}h`
    const m = Math.floor(ms / 60_000)
    return `expires in ${m}m`
  })()

  async function copy() {
    try {
      await navigator.clipboard.writeText(code)
      toast.success(`${code} copied to clipboard`)
    } catch {
      toast.error('Could not copy to clipboard')
    }
  }

  return (
    <Tooltip title={
      used
        ? 'This code has been redeemed'
        : expired
          ? 'This code expired — Resend will issue a new one'
          : `Click to copy${expiresIn ? ` · ${expiresIn}` : ''}`
    }>
      <Chip
        size="small"
        onClick={used || expired ? undefined : copy}
        icon={used || expired ? undefined : <ContentCopyOutlinedIcon sx={{ fontSize: 12 }} />}
        label={
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <span style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>
              {code}
            </span>
            <span style={{ opacity: 0.65, fontSize: 10, fontWeight: 700, letterSpacing: 0.4 }}>
              {tone.label.toUpperCase()}
            </span>
          </Stack>
        }
        sx={{
          height: 24, fontSize: 12,
          bgcolor: tone.bg, color: tone.fg,
          '& .MuiChip-icon': { color: tone.fg, ml: 0.75 },
          cursor: (used || expired) ? 'default' : 'pointer',
        }}
      />
    </Tooltip>
  )
}
